import { supabase } from './supabase'

/**
 * 198 (David, 14 Oct): the video counts its views, as TOTALS ONLY.
 *
 * Vimeo reports no views for our embed because `dnt=1` (lib/video.ts) turns
 * its stats off with its cookies, and David kept it that way. So the player
 * counts four stages itself and the database keeps a number per stage per
 * UTC day — never who.
 *
 *   started   the video began playing (muted autoplay included)
 *   sound     the viewer turned the sound on
 *   half      it reached the middle
 *   finished  it reached the end
 *
 * ONCE PER DEVICE, decided HERE. The server cannot dedupe without remembering
 * who called, which is the per-person read record rule 8 forbids — so the
 * browser remembers what it has counted, in localStorage, and nothing about
 * the person leaves it. A phone and a laptop are two; a crew's shared tablet
 * is one. Storage that throws (a private window) falls back to once per page.
 *
 * A failed send is not remembered on disk, so the next visit tries again; it
 * is remembered for the page, so a timeupdate every quarter-second cannot
 * hammer a failing endpoint. Viewing as someone else (176) always fails here —
 * that transaction is read-only — and that is the right outcome: it is not a
 * view.
 */
export type ViewStage = 'started' | 'sound' | 'half' | 'finished'

export const VIEW_STAGES: readonly ViewStage[] = ['started', 'sound', 'half', 'finished']

/** The localStorage key for one stage of one video on this device. */
export function viewKey(videoId: string, stage: ViewStage): string {
  return `oneconetec.videoView.${videoId}.${stage}`
}

/** Tried this page — sent or failed. Never cleared; a reload starts over. */
const triedThisPage = new Set<string>()

function storedOnDevice(key: string): boolean {
  try {
    return window.localStorage.getItem(key) === '1'
  } catch {
    return false
  }
}

function storeOnDevice(key: string): void {
  try {
    window.localStorage.setItem(key, '1')
  } catch {
    /* No storage: once per page is the best this device can do. */
  }
}

export async function sendView(videoId: string, stage: ViewStage): Promise<void> {
  const { error } = await supabase.rpc('record_video_view', { p_video_id: videoId, p_stage: stage })
  if (error) throw error
}

/**
 * Count one stage for this device, if it has not been counted. Returns whether
 * a count was sent and accepted. Never throws: a count that does not land is
 * a missing number, not an error anybody should see.
 */
export async function countView(
  videoId: string,
  stage: ViewStage,
  send: (videoId: string, stage: ViewStage) => Promise<void> = sendView,
): Promise<boolean> {
  if (!videoId) return false
  const key = viewKey(videoId, stage)
  if (triedThisPage.has(key) || storedOnDevice(key)) return false
  triedThisPage.add(key)
  try {
    await send(videoId, stage)
    storeOnDevice(key)
    return true
  } catch {
    return false
  }
}

/**
 * Which position stages a playhead has reached. `half` at 50%, `finished` at
 * 98% as well as on Vimeo's `ended` — a viewer who drags to the last second
 * and closes the tab has seen the end, and `ended` does not always fire.
 * A seek counts: someone who skips to the middle has reached the middle.
 */
export function stagesReached(seconds: number, duration: number): ViewStage[] {
  if (!(duration > 0) || !(seconds >= 0)) return []
  const at = seconds / duration
  const out: ViewStage[] = []
  if (at >= 0.5) out.push('half')
  if (at >= 0.98) out.push('finished')
  return out
}

export type ViewRow = { counted_on: string; stage: string; views: number }

export type ViewSummary = {
  total: Record<ViewStage, number>
  today: Record<ViewStage, number>
  /** The first UTC day with any count, or null when nothing is counted yet. */
  since: string | null
}

function zero(): Record<ViewStage, number> {
  return { started: 0, sound: 0, half: 0, finished: 0 }
}

/** The UTC date the database files a view under (198: rule 9). */
export function utcDay(d: Date = new Date()): string {
  return d.toISOString().slice(0, 10)
}

/**
 * Totals and today's figures from the read's rows. The read takes no limit, so
 * summing here is summing every row (standing rule 1's stated exception).
 */
export function summariseViews(rows: ViewRow[] | null, today: string = utcDay()): ViewSummary {
  const total = zero()
  const todays = zero()
  let since: string | null = null
  for (const r of rows ?? []) {
    if (!(VIEW_STAGES as readonly string[]).includes(r.stage)) continue
    const s = r.stage as ViewStage
    total[s] += r.views
    if (r.counted_on === today) todays[s] += r.views
    if (since === null || r.counted_on < since) since = r.counted_on
  }
  return { total, today: todays, since }
}

/** Whole-company analytics.read only; anyone else is refused by the server. */
export async function loadVideoViews(videoId: string): Promise<ViewSummary> {
  const { data, error } = await supabase.rpc('video_view_counts', { p_video_id: videoId })
  if (error) throw error
  return summariseViews(data as ViewRow[] | null)
}
